/**
 *
 * Copyright 2020-2026 Splunk Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */

import { diag } from '@opentelemetry/api'

import type { SpaMetricsMonitor } from '../../../types'

import { getPctMonitorTypes } from '../resource-monitor-types'
import { Monitor } from './monitor'

export class PerformanceMonitor extends Monitor {
	protected readonly monitorType: SpaMetricsMonitor = 'performance'

	private isMonitoring = false

	private observer: PerformanceObserver | null = null

	start(): void {
		if (this.isMonitoring) {
			diag.warn('PageLoadingManager.PerformanceMonitor: Already monitoring.')
			return
		}

		if (typeof PerformanceObserver === 'undefined') {
			diag.warn('PageLoadingManager.PerformanceMonitor: PerformanceObserver not available.')
			return
		}

		this.isMonitoring = true

		this.observer = new PerformanceObserver((list) => {
			for (const entry of list.getEntries()) {
				this.handleResourceEntry(entry as PerformanceResourceTiming)
			}
		})

		try {
			this.observer.observe({ buffered: true, type: 'resource' })
		} catch {
			this.observer.observe({ entryTypes: ['resource'] })
		}

		diag.debug('PageLoadingManager.PerformanceMonitor: Started monitoring resource timing.')
	}

	stop(): void {
		if (!this.isMonitoring) {
			return
		}

		this.observer?.disconnect()
		this.observer = null
		this.isMonitoring = false

		diag.debug('PageLoadingManager.PerformanceMonitor: Stopped monitoring.')
	}

	private handleResourceEntry(entry: PerformanceResourceTiming): void {
		const url = entry.name

		if (!getPctMonitorTypes(entry.initiatorType).includes(this.monitorType)) {
			return
		}

		const loadTime = entry.responseEnd - entry.startTime

		const event = Monitor.createDiscoveredEvent(url, entry.fetchStart)
		this.emitResourceStateChange(event)
		this.emitResourceStateChange(Monitor.createLoadedEvent(event.id, url, loadTime, entry.responseEnd))

		diag.debug('PageLoadingManager.PerformanceMonitor: Resource loaded', {
			initiatorType: entry.initiatorType,
			loadTime,
			url,
		})
	}
}
